<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import L from 'leaflet'
    import { isEqual, orderBy } from 'lodash-es'

    import { WDATA } from 'hydria/context'
    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import { escapeHtml } from 'hydria/utils/html'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'

    import { MAP_TILES } from 'admin/map'
    import { Breadcrumb } from 'breadcrumb'

    import Dropdown from 'hydria/components/Dropdown.svelte'
    import Modal from 'hydria/components/Modal.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        hastechnicaldelegs: '3state',
        hasrenewaldelegs: '3state',
        hascity: '3state',
        locked: '3state',
        active: '3state',

        has_subaccounts: '3state',
        is_subaccount: '3state',

        has_zone_price: '3state',
        has_tax_exemption: '3state',
        has_tld_price: '3state',
        has_admin_zone_slots: '3state',
        has_pmethod: 'boolstring',
    }
    const mappingOrder = ['q']

    // Search options for the backend
    interface SOptions {
        q: string
        hastechnicaldelegs: null | boolean
        hasrenewaldelegs: null | boolean
        hascity: null | boolean
        locked: null | boolean
        active: null | boolean

        has_subaccounts: null | boolean
        is_subaccount: null | boolean

        has_zone_price: null | boolean
        has_tax_exemption: null | boolean
        has_tld_price: null | boolean
        has_admin_zone_slots: null | boolean
        has_pmethod: null | boolean | string

        [key: string]: any
    }
    const defaults: SOptions = {
        q: '',
        hastechnicaldelegs: null,
        hasrenewaldelegs: null,
        hascity: null,
        locked: null,
        active: null,

        has_subaccounts: null,
        is_subaccount: null,

        has_zone_price: null,
        has_tax_exemption: null,
        has_tld_price: null,
        has_admin_zone_slots: null,
        has_pmethod: null,
    }

    // Search result from the server
    interface IGeoResult {
        id: string
        email: string
        lat: number
        lon: number
        name: string
        org: string
        postal_code: string
    }
    interface ISearchResult {
        items: IGeoResult[]
        total_items: number
        item_count: number
        excluded_ca: {
            items: IGeoResult[]
            total_items: number
            item_count: number
        }
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/wdom-users/map' : `/wdom-users/map?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/wdom-users/map' : `/wdom-users/map?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    interface IExtraMetaField {
        name: string
        tag: string
        type: 'choice'
        choices: {
            tag: string
            text: string
        }[]
    }
    // let extraMetaConfig: any
    let extraMetaFields: IExtraMetaField[] | undefined = $state()
    let extraReady = $state(false)

    async function loadExtraMetaConfig() {
        let r: any
        try {
            r = await simpleFetch('GET', '/json/wdom-user/1/extra-meta/config')
            // extraMetaConfig = r.data.config
            extraMetaFields = orderBy(
                r.data.fields.filter((d) => d.type == 'choice'),
                [(d) => r.data.field_order.indexOf(d.tag)],
                ['asc'],
            )
            for (const f of extraMetaFields) {
                f.choices.unshift({ tag: '!', text: '-- Sans réponse --' })
            }
            for (const emf of extraMetaFields) {
                mapping[`em-${emf.tag}`] = 'string'
                defaults[`em-${emf.tag}`] = ''
            }
            extraReady = true
        } catch (e) {
            console.error('Error loading extra meta:', e)
            notify({
                type: 'danger',
                title: 'Meta data',
                text: 'Erreur de chargement des méta données additionnelles',
            })
            extraMetaFields = []
        }
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                if (extraReady) loadQueryString($qs ? $qs : '')
            }),
        )
    })

    async function setup() {
        await loadExtraMetaConfig()
        loadQueryString($querystring)
    }
    setup()

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = true
    let searchId = 0
    // let searchQueryString: string
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = {}

        if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                case 'boolstring':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/wdom-user/1/map', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    const circleColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-primary')

    let nExclItems = $state(0)
    let exclResults = $state([] as any[])

    let excludedTsv = $state('')
    let excludedDownloadUrl = $state('')

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        nItems = data.total_items
        glayer.clearLayers()

        for (const item of data.items) {
            // const marker = L.circleMarker({ lat: item.geo.lat, lng: item.geo.lon }, { radius: 4, color: '#8c3dc7' })
            const marker = L.circleMarker({ lat: item.lat, lng: item.lon }, { radius: 4, color: circleColor })

            marker.bindPopup(`<s-anchor href="/wdom-user/${item.id}">${escapeHtml(item.name + (item.org ? ' (' + item.org + ')' : ''))}</s-anchor><br />${item.email}`)
            glayer.addLayer(marker)

            litems.push(item)
        }

        results.set(litems)

        nExclItems = data.excluded_ca.total_items
        exclResults = data.excluded_ca.items

        let showLines = [] as any[]

        for (const item of exclResults) {
            showLines.push([item.id, item.email, item.name, item.org, item.postal_code].join('\t'))
        }
        excludedTsv = showLines.join('\n')
        excludedDownloadUrl = URL.createObjectURL(new Blob([excludedTsv], { type: 'text/tab-separated-values' }))

        if (loading) loading = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
                setQueryString(sOptions)
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    let map: any
    let currentTileId: any = $state()
    let glayer: any
    onMount(() => {
        map = L.map('user-map').setView(WDATA['mapbox']['initial_center'], WDATA['mapbox']['initial_zoom'])

        setTile('light')
        glayer = L.layerGroup().addTo(map)

        // Don’t know why, but sometimes the tiles won’t load.
        // "clicking" on Light again solves it, so this will hopefully help.
        setTimeout(() => {
            setTile('light')
        }, 25)
    })

    function setTile(tileId: string) {
        if (currentTileId != null) map.removeLayer(MAP_TILES[currentTileId].layer)
        currentTileId = tileId
        map.addLayer(MAP_TILES[tileId].layer)
    }

    let excludedModal = $state(false)
    function showExcluded() {
        if (nExclItems) excludedModal = true
    }

    function copyExcludedToClipboard() {
        navigator.clipboard.writeText(excludedTsv).then(
            () => {
                notify({
                    type: 'success',
                    title: 'Utilisateurs Canadiens exclus',
                    text: $_('The text has been copied to the clipboard.'),
                })
            },
            () => {
                notify({
                    type: 'danger',
                    title: 'Utilisateurs Canadiens exclus',
                    text: $_('Could not copy the text.'),
                })
            },
        )
    }
</script>

<style>
    .search-extra-meta-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        margin: 0.25rem -0.25em 0;
    }
    .search-extra-meta-container > * {
        flex: 1 1 auto;
        max-width: 33%;
        min-width: 25%;
        padding: 0.25em;
    }

    .content-map-wrapper {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    .content-map-wrapper > * {
        flex: 0 0 auto;
    }
    .content-map-wrapper > .card-map {
        flex: 1 0 0;
        display: flex;
        flex-direction: column;

        margin-bottom: 0;
    }
    .card-map > .card-header {
        flex: 0 0 auto;
    }
    .card-map > .card-body {
        flex: 1 0 0;
        overflow: auto;
    }

    #user-map {
        width: 100%;
        /* height: 65vh; */
        height: 100%;
    }
    #user-map :global(.leaflet-popup-content) {
        max-height: 20ex;
        overflow: auto;
        cursor: pointer;
    }
    #user-map :global(.leaflet-popup-content blocquote) {
        margin: 0 0 0 1em;
        display: block;
        word-break: break-word;
    }

    .overlap {
        display: grid;
        align-items: center;
        justify-items: center;
    }
    .overlap > :global(*) {
        grid-column: 1/2;
        grid-row: 1/2;
    }
    .overlap > :global(*):first-child {
        opacity: 0.75;
    }
</style>

<div class="content-map-wrapper">
    <Breadcrumb
        crumbs={[
            { txt: $_('Utilisateurs'), url: spath('/wdom-users') },
            { icon: 'fa:map-o', txt: $_('Carte') },
        ]}
    />

    <div class="card mb-4">
        <div class="card-title text-primary">{$_('Carte des utilisateurs')}</div>
        <div class="card-body">
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    submit()
                }}
            >
                <div class="d-flex gap-2 flex-wrap">
                    <div class="col">
                        <div class="input-group">
                            <input type="text" class="form-control" bind:this={mainInput} bind:value={$text} />
                            {#if $text}
                                <button
                                    class="btn btn-secondary"
                                    type="button"
                                    disabled={updating}
                                    onclick={() => {
                                        $text = ''
                                        submit()
                                    }}><Icon inline icon="fa:times" /></button
                                >
                            {/if}
                            <button class="btn btn-secondary" type="submit" disabled={updating}>{$_('Filter')}</button>
                        </div>
                    </div>
                    <div class="btn-group">
                        <button type="button" class="btn btn-secondary" disabled>Tech :</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="A des délégués"
                            disabled={updating}
                            class:active={sOptions.hastechnicaldelegs === true}
                            onclick={() => {
                                sOptions.hastechnicaldelegs = sOptions.hastechnicaldelegs === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="pajamas:work-item-parent" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="Est un délégué"
                            disabled={updating}
                            class:active={sOptions.istechnicaldeleg === true}
                            onclick={() => {
                                sOptions.istechnicaldeleg = sOptions.istechnicaldeleg === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="pajamas:work-item-child" /></button
                        >
                    </div>
                    <div class="btn-group">
                        <button type="button" class="btn btn-secondary" disabled>Renew :</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="A des délégués"
                            disabled={updating}
                            class:active={sOptions.hasrenewaldelegs === true}
                            onclick={() => {
                                sOptions.hasrenewaldelegs = sOptions.hasrenewaldelegs === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="pajamas:work-item-parent" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="Est un délégué"
                            disabled={updating}
                            class:active={sOptions.isrenewaldeleg === true}
                            onclick={() => {
                                sOptions.isrenewaldeleg = sOptions.isrenewaldeleg === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="pajamas:work-item-child" /></button
                        >
                    </div>
                    <div class="btn-group">
                        <button type="button" class="btn btn-secondary" disabled><Icon inline icon="mdi:account" /></button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="A des sous-comptes"
                            disabled={updating}
                            class:active={sOptions.has_subaccounts === true}
                            onclick={() => {
                                sOptions.has_subaccounts = sOptions.has_subaccounts === true ? null : true
                                if (sOptions.has_subaccounts) sOptions.is_subaccount = null
                                setQueryString(sOptions)
                            }}><Icon inline icon="pajamas:work-item-parent" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="Est un sous-compte"
                            disabled={updating}
                            class:active={sOptions.is_subaccount === true}
                            onclick={() => {
                                sOptions.is_subaccount = sOptions.is_subaccount === true ? null : true
                                if (sOptions.is_subaccount) sOptions.has_subaccounts = null
                                setQueryString(sOptions)
                            }}><Icon inline icon="pajamas:work-item-child" /></button
                        >
                    </div>
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="Avec un nom de ville"
                            disabled={updating}
                            class:active={sOptions.hascity === true}
                            onclick={() => {
                                sOptions.hascity = sOptions.hascity === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:address-card" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="Sans un nom de ville"
                            disabled={updating}
                            class:active={sOptions.hascity === false}
                            onclick={() => {
                                sOptions.hascity = sOptions.hascity === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:address-card-o" /></button
                        >
                    </div>
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip={'Compte blocké\n(trop de tentatives,\nmauvais mot de passe)'}
                            disabled={updating}
                            class:active={sOptions.locked === true}
                            onclick={() => {
                                sOptions.locked = sOptions.locked === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:lock" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip="Compte non-blocké"
                            disabled={updating}
                            class:active={sOptions.locked === false}
                            onclick={() => {
                                sOptions.locked = sOptions.locked === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:unlock-alt" /></button
                        >
                    </div>
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-tooltip={'Compte actif\n(peut se connecter)'}
                            disabled={updating}
                            class:active={sOptions.active === true}
                            onclick={() => {
                                sOptions.active = sOptions.active === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:thumbs-up" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-left"
                            data-tooltip={'Compte inactif\n(ne peut pas se connecter)'}
                            disabled={updating}
                            class:active={sOptions.active === false}
                            onclick={() => {
                                sOptions.active = sOptions.active === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:thumbs-down" /></button
                        >
                    </div>
                </div>
                <div class="d-flex gap-2 flex-wrap mt-2">
                    <!-- Tax – Exemption -->
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip={'Taxes normales'}
                            disabled={updating}
                            class:active={sOptions.has_tax_exemption === false}
                            onclick={() => {
                                sOptions.has_tax_exemption = sOptions.has_tax_exemption === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="tabler:tax" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary overlap tt-below"
                            data-tooltip={'Sans taxation'}
                            disabled={updating}
                            class:active={sOptions.has_tax_exemption === true}
                            onclick={() => {
                                sOptions.has_tax_exemption = sOptions.has_tax_exemption === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="tabler:tax" /><Icon inline icon="lucide:slash" /></button
                        >
                    </div>
                    <!-- Zone – Custom Price -->
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip={'Prix spécial de zone'}
                            disabled={updating}
                            class:active={sOptions.has_zone_price === true}
                            onclick={() => {
                                sOptions.has_zone_price = sOptions.has_zone_price === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="mdi:database" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary overlap tt-below"
                            data-tooltip={'Prix normal de zone'}
                            disabled={updating}
                            class:active={sOptions.has_zone_price === false}
                            onclick={() => {
                                sOptions.has_zone_price = sOptions.has_zone_price === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="mdi:database" /><Icon inline icon="lucide:slash" /></button
                        >
                    </div>
                    <!-- Zone – Admin Slots -->
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip={'Publication arbitraire de zone (« admin » zone slots)'}
                            disabled={updating}
                            class:active={sOptions.has_admin_zone_slots === true}
                            onclick={() => {
                                sOptions.has_admin_zone_slots = sOptions.has_admin_zone_slots === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="lucide-lab:slot-card-credit" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary overlap tt-below"
                            data-tooltip={'Sans publication arbitraire de zone (pas de « admin » zone slots)'}
                            disabled={updating}
                            class:active={sOptions.has_admin_zone_slots === false}
                            onclick={() => {
                                sOptions.has_admin_zone_slots = sOptions.has_admin_zone_slots === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="lucide-lab:slot-card-credit" /><Icon inline icon="lucide:slash" /></button
                        >
                    </div>
                    <!-- Prix de TLD -->
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip={'Prix spéciaux selon le TLD'}
                            disabled={updating}
                            class:active={sOptions.has_tld_price === true}
                            onclick={() => {
                                sOptions.has_tld_price = sOptions.has_tld_price === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:globe" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary overlap tt-below"
                            data-tooltip={'Prix de domaines normaux'}
                            disabled={updating}
                            class:active={sOptions.has_tld_price === false}
                            onclick={() => {
                                sOptions.has_tld_price = sOptions.has_tld_price === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:globe" /><Icon inline icon="lucide:slash" /></button
                        >
                    </div>

                    <!-- Méthodes de paiement -->
                    <div class="btn-group">
                        <button type="button" class="btn btn-secondary" disabled><Icon inline icon="mdi:dollar" /></button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip={'Sans configuration spéciale\n(Méthode par défaut seulement)'}
                            disabled={updating}
                            class:active={sOptions.has_pmethod === false}
                            onclick={() => {
                                sOptions.has_pmethod = sOptions.has_pmethod === false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="mdi:do-not-disturb-alt" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip="Configuration particulière"
                            disabled={updating}
                            class:active={sOptions.has_pmethod === true}
                            onclick={() => {
                                sOptions.has_pmethod = sOptions.has_pmethod === true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="mdi:asterisk" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip="Carte de crédit"
                            disabled={updating}
                            class:active={sOptions.has_pmethod === 'credit card'}
                            onclick={() => {
                                sOptions.has_pmethod = sOptions.has_pmethod === 'credit card' ? null : 'credit card'
                                setQueryString(sOptions)
                            }}><Icon inline icon="mdi:credit-card" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip="Stripe"
                            disabled={updating}
                            class:active={sOptions.has_pmethod === 'stripe'}
                            onclick={() => {
                                sOptions.has_pmethod = sOptions.has_pmethod === 'stripe' ? null : 'stripe'
                                setQueryString(sOptions)
                            }}><Icon inline icon="cib:stripe" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip="Soumission"
                            disabled={updating}
                            class:active={sOptions.has_pmethod === 'quotation'}
                            onclick={() => {
                                sOptions.has_pmethod = sOptions.has_pmethod === 'quotation' ? null : 'quotation'
                                setQueryString(sOptions)
                            }}><Icon inline icon="mdi:offer" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-below"
                            data-tooltip="Paiement différé"
                            disabled={updating}
                            class:active={sOptions.has_pmethod === 'bill later'}
                            onclick={() => {
                                sOptions.has_pmethod = sOptions.has_pmethod === 'bill later' ? null : 'bill later'
                                setQueryString(sOptions)
                            }}><Icon inline icon="mdi:watch-later" /></button
                        >
                    </div>
                </div>
                {#if extraReady && extraMetaFields}
                    <div class="search-extra-meta-container">
                        {#each extraMetaFields as emf}
                            <div class="search-extra-meta-choice">
                                {#if emf.type == 'choice'}
                                    <Dropdown
                                        items={emf.choices}
                                        placeholder={emf.name}
                                        onSelect={(e) => {
                                            sOptions[`em-${emf.tag}`] = e
                                            setQueryString(sOptions)
                                        }}
                                        text={'text'}
                                        id={'tag'}
                                        selected={sOptions[`em-${emf.tag}`]}
                                        empty={''}
                                        clear={'Tout'}
                                        disabled={updating}
                                        alignRight={true}
                                        block={true}
                                    />
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}
            </form>
        </div>
    </div>

    <div class="card card-map">
        <div class="card-header text-bg-primary d-flex gap-2 align-items-center">
            <span class="col d-flex gap-4">
                <span>{$ngettext('{n} utilisateur', '{n} utilisateurs', { n: nItems })}</span>
                <span><button type="button" class="btn btn-link btn-a text-light" onclick={() => showExcluded()}>{$ngettext('{n} exclu', '{n} exclus', { n: nExclItems })}</button></span>
            </span>
            <div class="btn-group btn-sm">
                <button disabled type="button" class="btn btn-sm btn-secondary">Mapbox :</button>
                {#each Object.entries(MAP_TILES) as [id, tile]}
                    <button type="button" class="btn btn-sm btn-secondary" disabled={updating} class:active={id === currentTileId} onclick={() => setTile(id)}>{tile.name}</button>
                {/each}
            </div>
        </div>
        <div class="card-body">
            <div id="user-map"></div>
        </div>
    </div>
</div>
{#if excludedModal}
    <Modal close={() => (excludedModal = false)} modalClasses="modal-xl mde-modal">
        <div class="modal-header text-bg-primary">Utilisateurs Canadiens exclus</div>
        <div class="modal-body">
            <div class="responsive">
                <table style="width: 100%">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>email</th>
                            <th>Nom</th>
                            <th>Org</th>
                            <th>Code Postal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each exclResults as item}
                            <tr>
                                <td><a use:sanchor href={spath(`/wdom-user/${item.id}`)}>{item.id}</a></td>
                                <td>{item.email}</td>
                                <td>{item.name ? item.name : '–'}</td>
                                <td>{item.org ? item.org : '–'}</td>
                                <td>{item.postal_code}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        </div>
        <div class="modal-footer">
            <a href={excludedDownloadUrl} download="codes_postal_invalides.txt" class="btn btn-sm btn-secondary">Télécharger « TSV »</a>
            <button type="button" class="btn btn-sm btn-secondary" onclick={() => copyExcludedToClipboard()}>Copier en « TSV »</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => (excludedModal = false)}>{$_('Fermer')}</button>
        </div>
    </Modal>
{/if}
